import { mapKeys, merge } from '@vaaas/fpts/object';
import { head, last } from '@vaaas/fpts/array';
import { mapKeys as mmapKeys, concat_ip } from '@vaaas/fpts/map';
import { map, foldr } from '@vaaas/fpts/iter';
import { pipe } from '@vaaas/fpts/function';
import { LOG, display_message_to_user } from '@phi/logging/frontend';

export type KBHandler = (e: KeyboardEvent) => void;

export type KeyMap = {
    down: Map<number, KBHandler>;
    up: Map<number, KBHandler>;
}

// see USB HID device class definition, 8.3 Report Format for Array Items
const CTRL = 0b0001_0000_0000;
const SHIFT = 0b0010_0000_0000;
const ALT = 0b0100_0000_0000;
const GUI = 0b1000_0000_0000;
export const Fallthrough = 0b1111_1111_1111;

/** mapping of graphemes to geometric key codes */
const GRAPHEMES_KEYS: Record<string, string> = {
    'A': 'KeyA',
    'a': 'KeyA',
    'B': 'KeyB',
    'b': 'KeyB',
    'C': 'KeyC',
    'c': 'KeyC',
    'D': 'KeyD',
    'd': 'KeyD',
    'E': 'KeyE',
    'e': 'KeyE',
    'F': 'KeyF',
    'f': 'KeyF',
    'G': 'KeyG',
    'g': 'KeyG',
    'H': 'KeyH',
    'h': 'KeyH',
    'I': 'KeyI',
    'i': 'KeyI',
    'J': 'KeyJ',
    'j': 'KeyJ',
    'K': 'KeyK',
    'k': 'KeyK',
    'L': 'KeyL',
    'l': 'KeyL',
    'M': 'KeyM',
    'm': 'KeyM',
    'N': 'KeyN',
    'n': 'KeyN',
    'O': 'KeyO',
    'o': 'KeyO',
    'P': 'KeyP',
    'p': 'KeyP',
    'Q': 'KeyQ',
    'q': 'KeyQ',
    'R': 'KeyR',
    'r': 'KeyR',
    'S': 'KeyS',
    's': 'KeyS',
    'T': 'KeyT',
    't': 'KeyT',
    'U': 'KeyU',
    'u': 'KeyU',
    'V': 'KeyV',
    'v': 'KeyV',
    'W': 'KeyW',
    'w': 'KeyW',
    'X': 'KeyX',
    'x': 'KeyX',
    'Y': 'KeyY',
    'y': 'KeyY',
    'Z': 'KeyZ',
    'z': 'KeyZ',

    '1': 'Digit1',
    '!': 'Digit1',
    '2': 'Digit2',
    '@': 'Digit2',
    '3': 'Digit3',
    '#': 'Digit3',
    '4': 'Digit4',
    '$': 'Digit4',
    '5': 'Digit5',
    '%': 'Digit5',
    '6': 'Digit6',
    '^': 'Digit6',
    '7': 'Digit7',
    '&': 'Digit7',
    '8': 'Digit8',
    '*': 'Digit8',
    '9': 'Digit9',
    '(': 'Digit9',
    '0': 'Digit0',
    ')': 'Digit0',

    'Enter': 'Enter',
    'Escape': 'Escape',
    'Backspace': 'Backspace',
    'Tab': 'Tab',
    ' ': 'Space',

    'F1': 'F1',
    'F2': 'F2',
    'F3': 'F3',
    'F4': 'F4',
    'F5': 'F5',
    'F6': 'F6',
    'F7': 'F7',
    'F8': 'F8',
    'F9': 'F9',
    'F10': 'F10',
    'F11': 'F11',
    'F12': 'F12',

    'Delete': 'Delete',

    'ArrowRight': 'ArrowRight',
    'ArrowLeft': 'ArrowLeft',
    'ArrowDown': 'ArrowDown',
    'ArrowUp': 'ArrowUp',

    'Control': 'Control',
    'Shift': 'Shift',
    'Alt': 'Alt',
    'GUI': 'GUI',
    'Meta': 'GUI',
};


/** mapping of geometric key codes to numbers
 * see USB HID usable tables: Keyboard/Keypad Page (0x07)
 */
const KEYS_NUMBER: Record<string, number> = {
    'KeyA': 4,
    'KeyB': 5,
    'KeyC': 6,
    'KeyD': 7,
    'KeyE': 8,
    'KeyF': 9,
    'KeyG': 10,
    'KeyH': 11,
    'KeyI': 12,
    'KeyJ': 13,
    'KeyK': 14,
    'KeyL': 15,
    'KeyM': 16,
    'KeyN': 17,
    'KeyO': 18,
    'KeyP': 19,
    'KeyQ': 20,
    'KeyR': 21,
    'KeyS': 22,
    'KeyT': 23,
    'KeyU': 24,
    'KeyV': 25,
    'KeyW': 26,
    'KeyX': 27,
    'KeyY': 28,
    'KeyZ': 29,

    'Digit1': 30,
    'Digit2': 31,
    'Digit3': 32,
    'Digit4': 33,
    'Digit5': 34,
    'Digit6': 35,
    'Digit7': 36,
    'Digit8': 37,
    'Digit9': 38,
    'Digit0': 39,

    'Enter': 40,
    'Escape': 41,
    'Backspace': 42,
    'Tab': 43,
    'Space': 44,

    'F1': 58,
    'F2': 59,
    'F3': 60,
    'F4': 61,
    'F5': 62,
    'F6': 63,
    'F7': 64,
    'F8': 65,
    'F9': 66,
    'F10': 67,
    'F11': 68,
    'F12': 69,

    'Delete': 76,

    'ArrowRight': 79,
    'ArrowLeft': 80,
    'ArrowDown': 81,
    'ArrowUp': 82,

    'Control': 224,
    'Shift': 225,
    'Alt': 226,
    'GUI': 227,
};

const DESC_NUMBER: Record<string, number> = pipe(
    KEYS_NUMBER,
    mapKeys((x: string) => {
        if (x.startsWith('Key'))
            return x.slice(3).toLowerCase();
        else if (x.startsWith('Digit'))
            return x.slice(5);
        else
            return x;
    }),
    merge({ Fallthrough } as Record<string, number>),
);

function get_code(e: KeyboardEvent): string {
    return GRAPHEMES_KEYS[e.key] ?? e.code;
}

export function key_to_number(e: KeyboardEvent): number {
    let code: number = KEYS_NUMBER[get_code(e)] ?? 0;
    if (code >= 224 || code === 0) return code;
    if (e.ctrlKey) code = code | CTRL;
    if (e.shiftKey) code = code | SHIFT;
    if (e.altKey) code = code | ALT;
    if (e.metaKey) code = code | GUI;
    return code;
}

export function kbd(desc: string): number {
    const parts = desc.split('-');
    if (parts.length === 0) return 0;
    let code = DESC_NUMBER[last(parts)!] ?? 0;
    if (parts.length === 1) return code;
    for (const prefix of head(parts))
        switch(prefix) {
            case 'C': code = code | CTRL; break;
            case 'S': code = code | SHIFT; break;
            case 'A': code = code | ALT; break;
            case 'G': code = code | GUI; break;
            default: break;
        }
    return code;
}

export const make_keymap = mmapKeys(kbd)<KBHandler>;

export const merge_keymaps = (xs: Iterable<KeyMap>): KeyMap => ({
    down: pipe(xs, map(x => x.down), foldr(concat_ip<number, KBHandler>, new Map())),
    up: pipe(xs, map(x => x.up), foldr(concat_ip<number, KBHandler>, new Map()))
});

export function is_modifier(x: string) {
    switch (x) {
        case 'Shift':
        case 'Control':
        case 'Meta':
        case 'Alt':
            return true;
        default:
            return false;
    }
}

export function isEscape(x: KeyboardEvent): boolean {
    return x.keyCode === 27;
}

export function isEnter(x: KeyboardEvent) {
    return x.keyCode === 13;
}

export function isEnterOrSpace(x: KeyboardEvent): boolean {
    return x.keyCode === 32
        || x.keyCode === 13;
}

/** set of characters that are not accepted by onshape */
export const disallowed_symbols = new Set('/<>');

/** reject input events whose symbols are not allowed by onshape */
export function reject_symbols(e: KeyboardEvent) {
    if (disallowed_symbols.has(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        display_message_to_user(LOG.WARN, 'This character is not allowed in file names: ' + e.key);
        return false;
    } else return true;
}
